import { Suspense, type ReactNode } from 'react';
import * as s from './MobileWindowTakeover.css';
import { WindowSkeleton } from '../../atoms';

export interface MobileWindowTakeoverProps {
  windowId?: string;
  title: string;
  children: ReactNode;
  /** TitleBar 영역 title text 대체 ReactNode. 미주입 시 기본 title string 렌더. */
  titleSlot?: ReactNode;
  /**
   * 제목 앞자리 ReactNode (예: 상세에서 목록으로 돌아가는 버튼). heading 밖에 둬서
   * 제목의 접근성 이름에 버튼 라벨이 섞이지 않게 한다. 미주입 시 아무것도 안 그린다.
   */
  leadingSlot?: ReactNode;
  /** content 아래 footer band ReactNode. 미주입 시 footer band 자체 렌더 안 함. */
  footerSlot?: ReactNode;
  zIndex?: number;
  /** display:none 으로 숨김 — unmount 하지 않아 창 내부 상태를 보존한다. */
  hidden?: boolean;
  suspenseFallback?: ReactNode;
  onClose?: () => void;
}

/**
 * 모바일 전용 윈도우 래퍼 — 데스크톱 메타포(드래그·리사이즈·스냅·최소화) 없이
 * 윈도우 콘텐츠를 window plane 전체로 펼친다. 페이지급 콘텐츠는 풀스크린이 정당하다는
 * 근거: docs/review/mobile-modal-a11y-research-2026-07-17.md (콘텐츠 분량 기준 분기)
 */
export function MobileWindowTakeover({
  windowId,
  title,
  children,
  titleSlot,
  leadingSlot,
  footerSlot,
  zIndex = 10,
  hidden = false,
  suspenseFallback,
  onClose,
}: MobileWindowTakeoverProps) {
  return (
    <section
      className={s.takeover}
      style={{ zIndex, display: hidden ? 'none' : undefined }}
      data-mobile-window-takeover={windowId ?? ''}
      aria-label={title}
    >
      <header className={s.titleBar}>
        {leadingSlot}
        <h2 className={s.title} tabIndex={-1}>
          {titleSlot ?? title}
        </h2>
        <button type="button" className={s.closeButton} onClick={onClose} aria-label="닫기">
          &#x2715;
        </button>
      </header>
      <div className={s.content} data-mobile-window-content>
        <Suspense fallback={suspenseFallback ?? <WindowSkeleton />}>{children}</Suspense>
      </div>
      {footerSlot && <footer className={s.footer}>{footerSlot}</footer>}
    </section>
  );
}
